import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'

import { AuthContext } from '@/context/auth-context'
import OsobaService from '@/services/OsobaService'
import PlacowkaService from '@/services/PlacowkaService'
import RozmowaService from '@/services/RozmowaService'

const getRelationId = relation => {
	if (!relation) return ''
	if (typeof relation === 'object') return relation?._id || relation?.id || relation?.userid || ''
	return relation
}

const getRelationName = relation => {
	if (!relation || typeof relation !== 'object') return ''
	return relation?.imie_i_nazwisko || relation?.nazwa || relation?.alternative_name || relation?.name || relation?.email || ''
}

const getUserId = user => user?.userid || user?._id || user?.id || ''

const pad = value => String(value).padStart(2, '0')

const getCurrentDatetimeLocal = () => {
	const now = new Date()
	return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`
}

const toApiDate = value => {
	if (!value) return ''
	const date = new Date(value)
	return Number.isNaN(date.getTime()) ? '' : date.toISOString()
}

const defaultForm = {
	id_osoba: '',
	imie_i_nazwisko: '',
	dig_rozmowy: getCurrentDatetimeLocal(),
}

const emptyNewOsoba = {
	imie_i_nazwisko: '',
	funkcja: '',
	telefon: '',
	email: '',
}

export default function useRozmowaAddPage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const presetPlacowkaId = searchParams.get('id_placowka')
	const presetOsobaId = searchParams.get('id_osoba')
	const presetOsobaApplied = useRef(false)
	const createdOsobaRef = useRef(null)
	const { user } = useContext(AuthContext)
	const [form, setForm] = useState(defaultForm)
	const [osobaMode, setOsobaMode] = useState('existing')
	const [newOsoba, setNewOsoba] = useState(emptyNewOsoba)
	const [selectedPlacowka, setSelectedPlacowka] = useState(null)
	const [osoby, setOsoby] = useState([])
	const [loadingOsoby, setLoadingOsoby] = useState(false)
	const [osobyError, setOsobyError] = useState(null)
	const [submitting, setSubmitting] = useState(false)

	const handleCancel = useCallback(() => {
		navigate('/komunikacja/rozmowy')
	}, [navigate])

	const handleChange = useCallback((field, value) => {
		setForm(prev => ({
			...prev,
			[field]: value,
		}))
	}, [])

	const loadOsoby = useCallback(async placowkaId => {
		if (!placowkaId) {
			setOsoby([])
			setOsobyError(null)
			return
		}

		setLoadingOsoby(true)
		setOsobyError(null)
		const result = await OsobaService.getByPlacowkaId(placowkaId)

		if (result.error) {
			setOsoby([])
			setOsobyError(result.error)
		} else {
			setOsoby(Array.isArray(result.data) ? result.data : [])
		}

		setLoadingOsoby(false)
	}, [])

	const handleSelectPlacowka = useCallback(placowka => {
		const placowkaId = getRelationId(placowka)

		setSelectedPlacowka(placowka)
		setForm(prev => ({
			...prev,
			id_osoba: '',
			imie_i_nazwisko: '',
		}))
		loadOsoby(placowkaId)
	}, [loadOsoby])

	const handleClearPlacowka = useCallback(() => {
		setSelectedPlacowka(null)
		setOsoby([])
		setOsobyError(null)
		setForm(prev => ({
			...prev,
			id_osoba: '',
			imie_i_nazwisko: '',
		}))
	}, [])

	useEffect(() => {
		if (!presetPlacowkaId) return undefined

		let alive = true

		;(async () => {
			const result = await PlacowkaService.getById(presetPlacowkaId)
			if (!alive || result?.error || !result?.data) return

			const placowka = result.data
			const placowkaId = getRelationId(placowka)

			setSelectedPlacowka(placowka)
			setForm(prev => ({
				...prev,
				id_osoba: '',
				imie_i_nazwisko: '',
			}))
			loadOsoby(placowkaId)
		})()

		return () => {
			alive = false
		}
	}, [presetPlacowkaId, loadOsoby])

	useEffect(() => {
		if (presetOsobaApplied.current || !presetOsobaId || osoby.length === 0) return

		const selectedOsoba = osoby.find(osoba => String(getRelationId(osoba)) === String(presetOsobaId))
		if (!selectedOsoba) return

		presetOsobaApplied.current = true
		setForm(prev => {
			if (prev.id_osoba) return prev
			return {
				...prev,
				id_osoba: getRelationId(selectedOsoba),
				imie_i_nazwisko: getRelationName(selectedOsoba),
			}
		})
	}, [osoby, presetOsobaId])

	const handleSelectOsoba = useCallback(osobaId => {
		const selectedOsoba = osoby.find(osoba => getRelationId(osoba) === osobaId)

		setOsobaMode('existing')
		setForm(prev => ({
			...prev,
			id_osoba: osobaId,
			imie_i_nazwisko: getRelationName(selectedOsoba),
		}))
	}, [osoby])

	const handleOsobaModeChange = useCallback(mode => {
		setOsobaMode(mode === 'new' ? 'new' : 'existing')
		if (mode === 'new') {
			setForm(prev => ({
				...prev,
				id_osoba: '',
				imie_i_nazwisko: '',
			}))
		}
	}, [])

	const handleNewOsobaChange = useCallback((field, value) => {
		setOsobaMode('new')
		setNewOsoba(prev => ({
			...prev,
			[field]: value,
		}))
		setForm(prev => ({
			...prev,
			id_osoba: '',
			imie_i_nazwisko: '',
		}))
	}, [])

	const handleSubmit = useCallback(async event => {
		event.preventDefault()
		if (submitting) return

		const placowkaId = getRelationId(selectedPlacowka)
		const userId = getUserId(user)

		if (!placowkaId) {
			await Swal.fire(t('conversations.messages.errorTitle'), t('conversations.add.messages.placowkaRequired'), 'error')
			return
		}

		if (!userId) {
			await Swal.fire(t('conversations.messages.errorTitle'), t('conversations.add.messages.userRequired'), 'error')
			return
		}

		const draft = {
			imie_i_nazwisko: newOsoba.imie_i_nazwisko.trim(),
			funkcja: newOsoba.funkcja.trim(),
			telefon: newOsoba.telefon.trim(),
			email: newOsoba.email.trim(),
		}
		const creatingOsoba = osobaMode === 'new'

		if (!creatingOsoba && !form.id_osoba) {
			await Swal.fire(t('conversations.messages.errorTitle'), t('conversations.add.messages.personRequired'), 'error')
			return
		}

		if (creatingOsoba && (!draft.imie_i_nazwisko || !draft.funkcja || !draft.telefon)) {
			await Swal.fire(t('conversations.messages.errorTitle'), t('conversations.add.messages.newPersonRequired'), 'error')
			return
		}

		setSubmitting(true)

		let osobaId = form.id_osoba
		let osobaName = form.imie_i_nazwisko

		if (creatingOsoba) {
			const draftKey = JSON.stringify({ ...draft, id_placowka: placowkaId })
			const alreadyCreated = createdOsobaRef.current?.draftKey === draftKey
				? createdOsobaRef.current
				: null

			if (alreadyCreated) {
				osobaId = alreadyCreated.id
				osobaName = alreadyCreated.name
			} else {
				const osobaResult = await OsobaService.create({
					...draft,
					id_placowka: placowkaId,
				})

				if (!osobaResult.success) {
					await Swal.fire(
						t('conversations.messages.errorTitle'),
						osobaResult.error || t('conversations.add.messages.newPersonFailed'),
						'error'
					)
					setSubmitting(false)
					return
				}

				osobaId = getRelationId(osobaResult.data)
				osobaName = osobaResult.data?.imie_i_nazwisko || draft.imie_i_nazwisko

				if (!osobaId) {
					await Swal.fire(
						t('conversations.messages.errorTitle'),
						t('conversations.add.messages.newPersonFailed'),
						'error'
					)
					setSubmitting(false)
					return
				}

				createdOsobaRef.current = { draftKey, id: osobaId, name: osobaName }
			}
		}

		const payload = {
			id_user: userId,
			id_placowka: placowkaId,
			id_osoba: osobaId,
			imie_i_nazwisko: osobaName,
			dig_rozmowy: toApiDate(form.dig_rozmowy),
		}

		const result = await RozmowaService.create(payload)

		if (!result.success) {
			await Swal.fire(
				t('conversations.messages.errorTitle'),
				result.error || t('conversations.add.messages.createFailed'),
				'error'
			)
			setSubmitting(false)
			return
		}

		setSubmitting(false)

		const rozmowaId = result.data?._id || result.data?.id
		navigate(rozmowaId ? `/komunikacja/rozmowy/details/${rozmowaId}` : '/komunikacja/rozmowy')
	}, [form, navigate, newOsoba, osobaMode, selectedPlacowka, submitting, t, user])

	return {
		form,
		handleCancel,
		handleChange,
		handleClearPlacowka,
		handleNewOsobaChange,
		handleOsobaModeChange,
		handleSelectOsoba,
		handleSelectPlacowka,
		handleSubmit,
		loadingOsoby,
		newOsoba,
		osobaMode,
		osoby,
		osobyError,
		selectedPlacowka,
		submitting,
	}
}
